import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 2 }
const _hoisted_3 = { class: "mt-3 text-center" }
const _hoisted_4 = { class: "left-items" }
const _hoisted_5 = { class: "right-items" }

import spinner from "@common/components/Spinner.vue";
import { useToast } from "@common/composable/toast";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { SourceType } from "@medapro-types/ChannelCreator/channel-creator-enum";
import catalogApi from "@services/catalogs.js";
import { computed, inject, onBeforeUnmount, onMounted, reactive, ref, unref, watch } from "vue";
import { useI18n } from "vue-i18n";
import AdvancedSearch from "../../../AdvancedSearch/views/AdvancedSearch.vue";
import CatalogSelection from "./GenerateCatalog/CatalogSelection.vue";

//#region Inject
type ViewModel = {
  sourceCatalogId: string;
  targetCatalogName: string;
  errorMessage: string;
  advancedSearchId: string;
  isCatalogSelection: boolean;
  isGeneratingCatalog: boolean;
  isLastPage: boolean;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'GenerateCatalogModal',
  props: {
    value: { type: Boolean },
    sourceType: {}
  },
  emits: ["input"],
  setup(__props: any, { emit: emits }) {

const props = __props;

const $getConnectionId = inject<() => string | null>("$getConnectionId");
const $subscribeToServerEvent = inject<(event: string, handler: any) => void>("$subscribeToServerEvent");
const $unsubscribeFromServerEvent = inject<(event: string, handler: any) => void>("$unsubscribeFromServerEvent");
const $signalRStartedPromise = inject<Promise<void>>("$signalRStartedPromise");
//#endregion

//#region General
const { createToast } = useToast();


const { primaryLanguageCode } = useContentLanguages();
const { t: $t, te: $te } = useI18n();

const localModel = computed({
  get() {
    return props.value;
  },
  set(nv) {
    emits("input", nv);
  }
});
//#endregion

//#region viewmodel
const vm = reactive<ViewModel>({
  sourceCatalogId: "",
  targetCatalogName: "",
  errorMessage: "",
  advancedSearchId: "",
  isCatalogSelection: true,
  isGeneratingCatalog: false,
  isLastPage: false
});
//#endregion

//#region Refs
const advancedSearch = ref<InstanceType<typeof AdvancedSearch> | null>(null);
const catalogSelection = ref<InstanceType<typeof CatalogSelection> | null>(null);
//#endregion

const isModalValid = computed(() => {
  if (props.sourceType == SourceType.Filter) {
    const advSearchObj = unref(advancedSearch);
    const catalogSelectionObj = unref(catalogSelection);
    return (
      (vm.isCatalogSelection && vm.sourceCatalogId && vm.targetCatalogName && catalogSelectionObj?.state.isValid) ||
      (vm.isLastPage && vm.sourceCatalogId && vm.targetCatalogName && vm.advancedSearchId && !advSearchObj?.states?.isDirty)
    );
  } else {
    return true;
  }
});

const nextOrGenerate = async () => {
  if (vm.isCatalogSelection && !vm.isLastPage) {
    vm.isCatalogSelection = false;
    vm.isLastPage = true;
  } else {
    if (props.sourceType == SourceType.Filter) {
      try {
        vm.isGeneratingCatalog = true;
        await catalogApi.generateCatalogFromFilter(
          vm.sourceCatalogId,
          vm.targetCatalogName,
          vm.advancedSearchId,
          primaryLanguageCode.value,
          $getConnectionId ? ($getConnectionId() ?? "") : ""
        );
      } catch (error: any) {
        if (error) {
          console.error(`Catalog generation failure  with error - "${error}" `);
          let errorMessage = "";
          if ($te("ErrorMessage." + error.response.data?.value)) errorMessage = $t("ErrorMessage." + error.response.data?.value);
          else errorMessage = error.response.data?.value;
          createToast({
            text: errorMessage,
            title: $t("ErrorMessage.CatalogueGenerationFailed"),
            variant: "warning",
            autoClose: false
          });
        }
      }
    }
  }
};

const back = () => {
  vm.isCatalogSelection = true;
  vm.isLastPage = false;
};

const hideDialog = () => {
  clearDialog();
  localModel.value = false;
};

const clearDialog = () => {
  vm.isCatalogSelection = true;
  vm.isLastPage = false;
  vm.advancedSearchId = "";
  vm.sourceCatalogId = "";
  vm.targetCatalogName = "";
};

watch(
  () => localModel.value,
  (nv) => {
    if (!nv && $unsubscribeFromServerEvent) {
      unSubscribeEvent();
    }
  },
  { deep: true }
);

function onCatalogGenerationFromFilterCompleted() {
  vm.isGeneratingCatalog = false;
  clearDialog();
  localModel.value = false;
}

async function startEventSubscription() {
  await $signalRStartedPromise;
  if ($subscribeToServerEvent) {
    $subscribeToServerEvent("CatalogGenerationFromFilterCompleted", onCatalogGenerationFromFilterCompleted);
  }
}

async function unSubscribeEvent() {
  if ($unsubscribeFromServerEvent) {
    $unsubscribeFromServerEvent("CatalogGenerationFromFilterCompleted", onCatalogGenerationFromFilterCompleted);
  }
}

//#region LifeCycle
onMounted(() => {
  startEventSubscription();
  clearDialog();
});

onBeforeUnmount(() => {
  unSubscribeEvent();
});
//#endregion

return (_ctx: any,_cache: any) => {
  const _component_b_overlay = _resolveComponent("b-overlay")!
  const _component_b_btn = _resolveComponent("b-btn")!
  const _component_b_modal = _resolveComponent("b-modal")!

  return (_openBlock(), _createBlock(_component_b_modal, {
    title: _unref($t)('menubar.CatalogGen'),
    id: "catalog-generate-modal",
    "header-close-content": "",
    size: "xl",
    "body-class": "advanced-search-modal",
    modelValue: localModel.value,
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((localModel).value = $event)),
    onOk: _withModifiers(nextOrGenerate, ["prevent"]),
    onCancel: hideDialog,
    onClose: hideDialog,
    "no-close-on-backdrop": "",
    "no-close-on-esc": ""
  }, {
    "modal-footer": _withCtx(({ ok, cancel }) => [
      _createElementVNode("div", _hoisted_4, [
        (vm.isLastPage)
          ? (_openBlock(), _createBlock(_component_b_btn, {
              key: 0,
              disabled: vm.isGeneratingCatalog,
              onClick: back
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref($t)("back")), 1)
              ]),
              _: 1
            }, 8, ["disabled"]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(_component_b_btn, {
          disabled: !isModalValid.value || vm.isGeneratingCatalog,
          onClick: _withModifiers(ok, ["prevent"])
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(!vm.isLastPage ? _unref($t)("next") : _unref($t)("generate")), 1)
          ]),
          _: 2
        }, 1032, ["disabled", "onClick"]),
        _createVNode(_component_b_btn, { onClick: cancel }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref($t)("Close")), 1)
          ]),
          _: 2
        }, 1032, ["onClick"])
      ])
    ]),
    default: _withCtx(() => [
      (props.sourceType == _unref(SourceType).Filter)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            (vm.isCatalogSelection)
              ? (_openBlock(), _createBlock(CatalogSelection, {
                  key: 0,
                  ref_key: "catalogSelection",
                  ref: catalogSelection,
                  sourceCatalogId: vm.sourceCatalogId,
                  "onUpdate:sourceCatalogId": _cache[0] || (_cache[0] = ($event: any) => ((vm.sourceCatalogId) = $event)),
                  targetCatalogName: vm.targetCatalogName,
                  "onUpdate:targetCatalogName": _cache[1] || (_cache[1] = ($event: any) => ((vm.targetCatalogName) = $event))
                }, null, 8, ["sourceCatalogId", "targetCatalogName"]))
              : _createCommentVNode("", true),
            (vm.isLastPage)
              ? (_openBlock(), _createBlock(AdvancedSearch, {
                  key: 1,
                  ref_key: "advancedSearch",
                  ref: advancedSearch,
                  hideExcelEdit: true,
                  catalogId: vm.sourceCatalogId,
                  advancedSearchId: vm.advancedSearchId,
                  "onUpdate:advancedSearchId": _cache[2] || (_cache[2] = ($event: any) => ((vm.advancedSearchId) = $event))
                }, null, 8, ["catalogId", "advancedSearchId"]))
              : _createCommentVNode("", true),
            (vm.isGeneratingCatalog)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _createVNode(_component_b_overlay, {
                    "z-index": "20",
                    opacity: "1",
                    variant: "white",
                    "no-wrap": "",
                    show: vm.isGeneratingCatalog
                  }, {
                    overlay: _withCtx(() => [
                      _createElementVNode("div", _hoisted_3, _toDisplayString(_unref($t)("CatalogGenerationRunningInBackground")), 1),
                      _createVNode(spinner, { size: "48" })
                    ]),
                    _: 1
                  }, 8, ["show"])
                ]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["title", "modelValue", "onOk"]))
}
}

})